// ไฟล์: frontend/src/pages/UploadInnovationPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './UploadInnovationPage.css';

const UploadInnovationPage = ({ user }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category_id: '',
    subject_id: '',
    grade_level_id: ''
  });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch categories, subjects, and grade levels
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [categoriesRes, subjectsRes, gradeLevelsRes] = await Promise.all([
          axios.get('/categories'),
          axios.get('/subjects'),
          axios.get('/grade-levels')
        ]);
        
        setCategories(categoriesRes.data.categories);
        setSubjects(subjectsRes.data.subjects);
        setGradeLevels(gradeLevelsRes.data.gradeLevels);
      } catch (error) {
        console.error('Error fetching form data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลฟอร์มได้'
        });
      }
    };

    fetchFormData();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // ตรวจสอบขนาดไฟล์ (ไม่เกิน 15MB)
      if (selectedFile.size > 15 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          file: 'ขนาดไฟล์ต้องไม่เกิน 15MB'
        }));
        return;
      }
      
      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  // Handle thumbnail change
  const handleThumbnailChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // ตรวจสอบขนาดไฟล์ (ไม่เกิน 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          thumbnail: 'ขนาดรูปภาพต้องไม่เกิน 5MB'
        }));
        return;
      }
      
      // ตรวจสอบประเภทไฟล์
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(selectedFile.type)) {
        setErrors(prev => ({
          ...prev,
          thumbnail: 'รองรับเฉพาะไฟล์ JPEG, JPG, และ PNG เท่านั้น'
        }));
        return;
      }
      
      setThumbnail(selectedFile);
      setErrors(prev => ({ ...prev, thumbnail: '' }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'กรุณาระบุชื่อนวัตกรรม';
    if (!formData.description.trim()) newErrors.description = 'กรุณาระบุรายละเอียดสั้นๆ';
    if (!formData.content.trim()) newErrors.content = 'กรุณาระบุเนื้อหา/รายละเอียดนวัตกรรม';
    if (!formData.category_id) newErrors.category_id = 'กรุณาเลือกหมวดหมู่';
    if (!formData.subject_id) newErrors.subject_id = 'กรุณาเลือกวิชา';
    if (!formData.grade_level_id) newErrors.grade_level_id = 'กรุณาเลือกระดับชั้น';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstError);
      if (errorElement) errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('subject_id', formData.subject_id);
      formDataToSend.append('grade_level_id', formData.grade_level_id);
      
      if (file) formDataToSend.append('file', file);
      if (thumbnail) formDataToSend.append('thumbnail', thumbnail);
      
      const response = await axios.post('/innovations', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: response.data.message,
        confirmButtonText: 'ตกลง'
      }).then(() => {
        navigate('/dashboard');
      });
      
    } catch (error) {
      console.error('Error uploading innovation:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถอัปโหลดนวัตกรรมได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-innovation-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-lightbulb"></i> แชร์นวัตกรรมการสอน
          </h1>
          <p className="page-description">
            แบ่งปันนวัตกรรมและแนวปฏิบัติที่ดีของคุณกับเพื่อนครูทั่วประเทศ เพื่อร่วมกันพัฒนาการศึกษาไทย
          </p>
        </div>
        
        <div className="card">
          <div className="card-header">
            <i className="fas fa-lightbulb"></i> ข้อมูลนวัตกรรม
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title" className="form-label">ชื่อนวัตกรรม *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  placeholder="ระบุชื่อนวัตกรรมการสอน"
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="description" className="form-label">รายละเอียดสั้นๆ *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  placeholder="อธิบายรายละเอียดสั้นๆ เกี่ยวกับนวัตกรรมนี้"
                  rows="2"
                ></textarea>
                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
              </div>
              
              <div className="form-row">
                <div className="form-group col-md-12">
                  <label htmlFor="category_id" className="form-label">หมวดหมู่ *</label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className={`form-select ${errors.category_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกหมวดหมู่</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                </div>
                
                <div className="form-group col-md-12">
                  <label htmlFor="subject_id" className="form-label">วิชา *</label>
                  <select
                    id="subject_id"
                    name="subject_id"
                    value={formData.subject_id}
                    onChange={handleChange}
                    className={`form-select ${errors.subject_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกวิชา</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                  {errors.subject_id && <div className="invalid-feedback">{errors.subject_id}</div>}
                </div>
                
                <div className="form-group col-md-12">
                  <label htmlFor="grade_level_id" className="form-label">ระดับชั้น *</label>
                  <select
                    id="grade_level_id"
                    name="grade_level_id"
                    value={formData.grade_level_id}
                    onChange={handleChange}
                    className={`form-select ${errors.grade_level_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกระดับชั้น</option>
                    {gradeLevels.map(gradeLevel => (
                      <option key={gradeLevel.id} value={gradeLevel.id}>{gradeLevel.name}</option>
                    ))}
                  </select>
                  {errors.grade_level_id && <div className="invalid-feedback">{errors.grade_level_id}</div>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="content" className="form-label">เนื้อหา/รายละเอียดนวัตกรรม *</label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className={`form-control ${errors.content ? 'is-invalid' : ''}`}
                  placeholder="อธิบายรายละเอียดเกี่ยวกับนวัตกรรม วิธีการใช้งาน ประโยชน์ และผลลัพธ์ที่ได้จากการใช้นวัตกรรมนี้"
                  rows="8"
                ></textarea>
                {errors.content && <div className="invalid-feedback">{errors.content}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="file" className="form-label">ไฟล์ประกอบ (ไม่บังคับ)</label>
                <input
                  type="file"
                  id="file"
                  name="file"
                  onChange={handleFileChange}
                  className={`form-control ${errors.file ? 'is-invalid' : ''}`}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip"
                />
                <small className="form-text text-muted">
                  อัปโหลดไฟล์ประกอบนวัตกรรม (PDF, Word, PowerPoint, Excel, ZIP) ขนาดไม่เกิน 15MB
                </small>
                {errors.file && <div className="invalid-feedback">{errors.file}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="thumbnail" className="form-label">รูปภาพหน้าปก (ไม่บังคับ)</label>
                <input
                  type="file"
                  id="thumbnail"
                  name="thumbnail"
                  onChange={handleThumbnailChange}
                  className={`form-control ${errors.thumbnail ? 'is-invalid' : ''}`}
                  accept="image/jpeg,image/png,image/jpg"
                />
                <small className="form-text text-muted">
                  อัปโหลดรูปภาพหน้าปกสำหรับนวัตกรรม (JPEG, PNG) ขนาดไม่เกิน 5MB
                </small>
                {errors.thumbnail && <div className="invalid-feedback">{errors.thumbnail}</div>}
              </div>
              
              <div className="form-notice">
                <div className="notice-content">
                  <i className="fas fa-info-circle"></i>
                  <div>
                    <strong>หมายเหตุ:</strong> นวัตกรรมที่อัปโหลดจะต้องผ่านการอนุมัติจากผู้ดูแลระบบก่อนจึงจะแสดงบนเว็บไซต์
                  </div>
                </div>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> บันทึกนวัตกรรม
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-light" 
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  <i className="fas fa-arrow-left"></i> ย้อนกลับ
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadInnovationPage;